import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import type { ZoneRulesTable } from '@/lib/time'
import { useAuth } from '@/lib/auth/use-auth'
import { getReferenceProvider } from './index'
import { ReferenceContext, type ReferenceContextValue, type ReferenceStatus } from './reference-context'
import {
  REFERENCE_TABLES,
  type Aircraft,
  type Airport,
  type AirportCapability,
  type AirportGroup,
  type CapabilityException,
  type CityPair,
  type CityPairCapability,
  type Country,
  type CountryCapability,
  type CountryGroup,
  type FieldsOf,
  type ReferenceManifest,
  type ReferenceProvider as Provider,
  type ReferenceTableName,
  type Timezone,
} from './types'

const NO_PENDING = Object.fromEntries(
  REFERENCE_TABLES.map((table) => [table, 0]),
) as Record<ReferenceTableName, number>

/**
 * Loads reference data once per session and keeps it in memory.
 *
 * Mounted inside the authenticated shell rather than at the app root: the
 * sign-in screen has no use for 8.672 airports, and the seed is only fetched
 * when somebody actually reaches a screen that needs it.
 */
export function ReferenceDataProvider({ children }: { children: ReactNode }) {
  const { session } = useAuth()
  const actor = session?.user.email ?? 'unknown'

  const providerRef = useRef<Provider | null>(null)
  const [status, setStatus] = useState<ReferenceStatus>('loading')
  const [providerName, setProviderName] = useState('')
  const [manifest, setManifest] = useState<ReferenceManifest | null>(null)
  const [countries, setCountries] = useState<Country[]>([])
  const [timezones, setTimezones] = useState<Timezone[]>([])
  const [airports, setAirports] = useState<Airport[]>([])
  const [aircraft, setAircraft] = useState<Aircraft[]>([])
  const [cityPairs, setCityPairs] = useState<CityPair[]>([])
  const [cityPairCapability, setCityPairCapability] = useState<CityPairCapability[]>([])
  const [countryCapability, setCountryCapability] = useState<CountryCapability[]>([])
  const [airportCapability, setAirportCapability] = useState<AirportCapability[]>([])
  const [capabilityException, setCapabilityException] = useState<CapabilityException[]>([])
  const [airportGroups, setAirportGroups] = useState<AirportGroup[]>([])
  const [countryGroups, setCountryGroups] = useState<CountryGroup[]>([])
  const [zoneRules, setZoneRules] = useState<ZoneRulesTable>({})
  const [pending, setPending] = useState(NO_PENDING)

  const load = useCallback(async () => {
    const provider = providerRef.current ?? (await getReferenceProvider())
    providerRef.current = provider
    setProviderName(provider.name)

    const [
      country,
      timezone,
      airport,
      fleet,
      pairs,
      pairRules,
      countryRules,
      airportRules,
      exceptions,
      groupsByAirport,
      groupsByCountry,
      rules,
      meta,
      changes,
    ] = await Promise.all([
      provider.list('country'),
      provider.list('timezone'),
      provider.list('airport'),
      provider.list('aircraft'),
      provider.list('cityPair'),
      provider.list('cityPairCapability'),
      provider.list('countryCapability'),
      provider.list('airportCapability'),
      provider.list('capabilityException'),
      provider.list('airportGroup'),
      provider.list('countryGroup'),
      provider.zoneRules(),
      provider.manifest(),
      provider.pendingChanges(),
    ])

    setCountries(country)
    setTimezones(timezone)
    setAirports(airport)
    setAircraft(fleet)
    setCityPairs(pairs)
    setCityPairCapability(pairRules)
    setCountryCapability(countryRules)
    setAirportCapability(airportRules)
    setCapabilityException(exceptions)
    setAirportGroups(groupsByAirport)
    setCountryGroups(groupsByCountry)
    setZoneRules(rules)
    setManifest(meta)
    setPending(changes)
  }, [])

  useEffect(() => {
    let cancelled = false
    setStatus('loading')
    load()
      .then(() => {
        if (!cancelled) setStatus('ready')
      })
      .catch(() => {
        if (!cancelled) setStatus('error')
      })
    return () => {
      cancelled = true
    }
  }, [load])

  const refreshTable = useCallback(async (table: ReferenceTableName) => {
    const provider = providerRef.current
    if (!provider) return
    const [rows, changes] = await Promise.all([provider.list(table), provider.pendingChanges()])
    if (table === 'country') setCountries(rows as Country[])
    else if (table === 'timezone') setTimezones(rows as Timezone[])
    else if (table === 'airport') setAirports(rows as Airport[])
    else if (table === 'aircraft') setAircraft(rows as Aircraft[])
    else if (table === 'cityPair') setCityPairs(rows as CityPair[])
    else if (table === 'cityPairCapability') setCityPairCapability(rows as CityPairCapability[])
    else if (table === 'countryCapability') setCountryCapability(rows as CountryCapability[])
    else if (table === 'airportCapability') setAirportCapability(rows as AirportCapability[])
    else if (table === 'capabilityException') setCapabilityException(rows as CapabilityException[])
    else if (table === 'airportGroup') setAirportGroups(rows as AirportGroup[])
    else setCountryGroups(rows as CountryGroup[])
    setPending(changes)
  }, [])

  const create = useCallback(
    async <T extends ReferenceTableName>(table: T, fields: FieldsOf<T>) => {
      const provider = providerRef.current
      if (!provider) return
      await provider.create(table, fields, actor)
      await refreshTable(table)
    },
    [actor, refreshTable],
  )

  const update = useCallback(
    async <T extends ReferenceTableName>(table: T, id: string, fields: Partial<FieldsOf<T>>) => {
      const provider = providerRef.current
      if (!provider) return
      await provider.update(table, id, fields, actor)
      await refreshTable(table)
    },
    [actor, refreshTable],
  )

  const remove = useCallback(
    async (table: ReferenceTableName, id: string) => {
      const provider = providerRef.current
      if (!provider) return
      await provider.remove(table, id, actor)
      await refreshTable(table)
    },
    [actor, refreshTable],
  )

  const reset = useCallback(async () => {
    const provider = providerRef.current
    if (!provider) return
    await provider.reset()
    await load()
  }, [load])

  const value = useMemo<ReferenceContextValue>(
    () => ({
      status,
      providerName,
      manifest,
      countries,
      timezones,
      airports,
      aircraft,
      cityPairs,
      cityPairCapability,
      countryCapability,
      airportCapability,
      capabilityException,
      airportGroups,
      countryGroups,
      zoneRules,
      pending,
      create,
      update,
      remove,
      reset,
      reload: load,
    }),
    [
      status,
      providerName,
      manifest,
      countries,
      timezones,
      airports,
      aircraft,
      cityPairs,
      cityPairCapability,
      countryCapability,
      airportCapability,
      capabilityException,
      airportGroups,
      countryGroups,
      zoneRules,
      pending,
      create,
      update,
      remove,
      reset,
      load,
    ],
  )

  return <ReferenceContext.Provider value={value}>{children}</ReferenceContext.Provider>
}
